import { Component, EventEmitter, Output } from '@angular/core';
import { CellValueChangedEvent, ColDef, GridApi } from 'ag-grid-community';
import { ToastrService } from 'ngx-toastr';
import { Observable } from 'rxjs';
import { EmployeesImportResponse, EmployeesSaveRequest } from 'src/app/models/import/import.model';
import { ImportService } from 'src/app/services/imports/import.service';

@Component({
  selector: 'app-employees-import',
  templateUrl: './employees-import.component.html',
  styleUrls: ['./employees-import.component.scss']
})
export class EmployeesImportComponent {

  @Output() back = new EventEmitter<void>();
  @Output() importCompleted = new EventEmitter<void>();
  downloaded = false;
  uploaded = false;
  validated = false;
  employees: any[] = [];
  errors: string[] = [];
  rowData: any[] = [];
  allRowsValid = false;
  validationErrors: string[] = [];
  gridApi!: GridApi;
  roles: string[] = [];
  alreadyHaveTemplate = false;
  selectedDistId?: number;
  isValidating = false;
  isSaving = false;

  
  defaultColDef = {
    editable: true,
    sortable: true,
    filter: true,
    resizable: true,
    tooltipValueGetter: (params: any) => params.value
  };

  columnDefs: ColDef[] = [
    {
      headerName: 'First Name',
      field: 'firstName',
      editable: true,
      cellClass: params => params.data.errors?.some((e: string | string[]) => e.includes('First Name')) ? 'cell-error' : ''
    },
    {
      headerName: 'Last Name',
      field: 'lastName',
      editable: true,
      cellClass: params => params.data.errors?.some((e: string | string[]) => e.includes('Last Name')) ? 'cell-error' : ''
    },
    {
      headerName: 'Email',
      field: 'email',
      editable: true,
      cellClass: params => params.data.errors?.some((e: string | string[]) => e.includes('Email')) ? 'cell-error' : '',
      tooltipValueGetter: params => params.data.errors?.find((e: string | string[]) => e.includes('Email'))
    },
    {
      headerName: 'Role',
      field: 'role',
      editable: true,
      cellEditor: 'agSelectCellEditor',
      cellEditorParams: () => ({
        values: this.roles
      }),
      cellClass: params =>
        params.data.errors?.some((e: string | string[]) => e.includes('Role')) ? 'cell-error' : '',
      tooltipValueGetter: params =>
        params.data.errors?.find((e: string | string[]) => e.includes('Role'))
    },
    {
      headerName: 'Phone',
      field: 'phone',
      editable: true,
      cellClass: params => params.data.errors?.some((e: string | string[]) => e.includes('Phone')) ? 'cell-error' : '',
      tooltipValueGetter: params => params.data.errors?.find((e: string | string[]) => e.includes('Phone'))
    },
    {
      headerName: 'Address Street',
      field: 'addressStreet',
      editable: true,
      cellClass: params => params.data.errors?.some((e: string | string[]) => e.includes('Address Street')) ? 'cell-error' : ''
    },
    {
      headerName: 'City',
      field: 'city',
      editable: true,
      cellClass: params => params.data.errors?.some((e: string | string[]) => e.includes('City')) ? 'cell-error' : ''
    },
    {
      headerName: 'State/Province',
      field: 'state',
      editable: true,
      cellClass: params => params.data.errors?.some((e: string | string[]) => e.includes('State/Province')) ? 'cell-error' : '',
      tooltipValueGetter: params => params.data.errors?.find((e: string | string[]) => e.includes('State/Province'))
    },
    {
      headerName: 'Postal Code',
      field: 'postalCode',
      editable: true,
      cellClass: params => params.data.errors?.some((e: string | string[]) => e.includes('Postal code')) ? 'cell-error' : '',
      tooltipValueGetter: params => params.data.errors?.find((e: string | string[]) => e.includes('Postal code'))
    }
  ];


  constructor(private importService: ImportService,
    private toastService: ToastrService
  ) { }

  ngOnInit() {
    this.selectedDistId = Number(localStorage.getItem('distid')??"");

    this.importService.getEmployeeRoles().subscribe(list => {
      this.roles = list;
    });
  }


  downloadTemplate() {
    window.open('assets/templates/employees-template.xlsx', '_blank');
    this.downloaded = true;
  }

  onFileSelected(event: any) {
    const file = event.target.files[0];
    if (!file) return;

    this.isValidating = true;
    this.uploaded = true;

    (this.importService.upload('employees', file) as Observable<EmployeesImportResponse>)
      .subscribe({
        next: (response: EmployeesImportResponse) => {
          this.isValidating = false;
          this.validated = true;

          this.rowData = response.rows || [];

          for (const err of response.errors || []) {
            const row = this.rowData[err.rowIndex];
            if (row) {
              row.errors = err.messages;
            }
          }

          this.validationErrors = (response.errors || []).map(
            e => `Row ${e.rowIndex + 1}: ${e.messages.join(', ')}`
          );

          this.allRowsValid = this.rowData.every(r => !r.errors || r.errors.length === 0);

          if (!this.allRowsValid) {
            setTimeout(() => this.scrollToFirstError(), 50);
          }
        },

        error: () => {
          this.isValidating = false;
          this.validated = false;
          this.validationErrors = ['Upload failed. Please try again.'];
        }
      });

  }

  validateFile(file: File) {
    setTimeout(() => {
      this.validated = true;
      this.errors = [];
    }, 600);
  }

  onCellValueChanged(event: any) {
    this.gridApi.stopEditing();
    const row = event.data;

    row.errors = this.validateRow(row);

    event.api.refreshCells({
      rowNodes: [event.node],
      force: true
    });

    this.allRowsValid = this.rowData.every(r => r.errors.length === 0);

    if (!this.allRowsValid) {
      setTimeout(() => this.scrollToFirstError(), 50);
    }
  }

  onCellEditingStopped(event: any) {
    this.gridApi.stopEditing();
    const row = event.data;

    row.errors = this.validateRow(row);

    event.api.refreshCells({
      rowNodes: [event.node],
      force: true
    });

    this.allRowsValid = this.rowData.every(r => r.errors.length === 0);

    if (!this.allRowsValid) {
      setTimeout(() => this.scrollToFirstError(), 50);
    }
  }

  validateRow(row: any): string[] {
    const errors: string[] = [];

    if (!row.firstName?.trim()) errors.push('First Name is required');
    if (!row.lastName?.trim()) errors.push('Last Name is required');

    if (row.email && !this.isValidEmail(row.email.trim()))
      errors.push('Email format is invalid');

    if (!row.role?.trim())
      errors.push('Role is required');

    if (row.phone && !this.isValidPhone(row.phone))
      errors.push('Phone must be a valid 10‑digit number');

    if (row.state && !this.isValidRegion(row.state))
      errors.push('State/Province must be valid');

    if (row.postalCode && !this.isValidPostalCode(row.postalCode))
      errors.push('Postal code must be valid');

    return errors;
  }

  revalidateAll() {
    this.gridApi.stopEditing();
    this.validationErrors = [];

    for (const row of this.rowData) {
      row.errors = this.validateRow(row);

      if (row.errors.length > 0) {
        this.validationErrors.push(
          `Row ${row.rowNumber}: ${row.errors.join(', ')}`
        );
      }
    }

    this.allRowsValid = this.rowData.every(r => r.errors.length === 0);

    this.gridApi.refreshCells({ force: true });

    // Auto-scroll to first error
    if (!this.allRowsValid) {
      setTimeout(() => this.scrollToFirstError(), 50);
    }
  }


  onGridReady(params: any) {
    this.gridApi = params.api;
  }

  scrollToFirstError() {
    if (!this.gridApi || !this.rowData?.length) return;

    // Find first row with errors
    const firstInvalidRow = this.rowData.find(r => r.errors?.length > 0);
    if (!firstInvalidRow) return;

    const rowIndex = this.rowData.indexOf(firstInvalidRow);

    // Find first invalid column
    const firstError = firstInvalidRow.errors[0].toLowerCase();
    const col = this.columnDefs.find(c =>
      firstError.includes(c.field!.toLowerCase())
    );

    const colId = col?.field;

    // Scroll to row + column
    this.gridApi.ensureIndexVisible(rowIndex, 'middle');

    if (colId) {
      this.gridApi.ensureColumnVisible(colId);
      this.gridApi.setFocusedCell(rowIndex, colId);
    }
  }

  isValidEmail(email: string): boolean {
    if (!email) return true;
    const re = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return re.test(email);
  }

  isValidPhone(phone: string): boolean {
    if (!phone) return true;

    const digits = phone.replace(/\D/g, '');
    return digits.length === 10;
  }


  isValidRegion(region: string): boolean {
    if (!region) return true;

    const validRegions = [
      // US States
      "AL", "AK", "AZ", "AR", "CA", "CO", "CT", "DE", "FL", "GA", "HI", "ID", "IL", "IN", "IA",
      "KS", "KY", "LA", "ME", "MD", "MA", "MI", "MN", "MS", "MO", "MT", "NE", "NV", "NH", "NJ",
      "NM", "NY", "NC", "ND", "OH", "OK", "OR", "PA", "RI", "SC", "SD", "TN", "TX", "UT", "VT",
      "VA", "WA", "WV", "WI", "WY",
      // Canadian Provinces & Territories
      "AB", "BC", "MB", "NB", "NL", "NS", "NT", "NU", "ON", "PE", "QC", "SK", "YT"
    ];

    return validRegions.includes(region.toUpperCase());
  }

  isValidPostalCode(postal: string): boolean {
    if (!postal) return true;

    postal = postal.trim().toUpperCase();

    // US ZIP
    if (/^\d{5}(-?\d{4})?$/.test(postal)) return true;

    // Canadian Postal Code
    if (/^[ABCEGHJ-NPRSTVXY]\d[ABCEGHJ-NPRSTV-Z] ?\d[ABCEGHJ-NPRSTV-Z]\d$/.test(postal))
      return true;

    return false;
  }

  saveEmployees() {
    this.isSaving = true;
  const payload: EmployeesSaveRequest = {
    employees: this.rowData.map(e => ({
      rowNumber: e.rowNumber,
      firstName: e.firstName?.trim() || null,
      lastName: e.lastName?.trim() || null,
      email: e.email?.trim() || null,
      phone: e.phone?.trim() || null,
      role: e.role?.trim() || null, 
      addressStreet: e.addressStreet?.trim() || null, 
      city: e.city?.trim() || null, 
      state: e.state?.trim() || null, 
      postalCode: e.postalCode?.trim() || null
    })),
    importType: "Employees",
    distributorId: this.selectedDistId! 
  };

  this.importService.saveEmployees(payload).subscribe({
    next: (response) => {
      this.toastService.success("Import completed successfully");
      this.isSaving = false;
      this.importCompleted.emit();
    },
    error: (err) => {
      console.error("Save failed", err);
      const message = err.error?.error || 'An unexpected error occurred.'; 
      this.isSaving = false;
      this.toastService.error(message);
    }
  });
}


  goBack() {
    this.back.emit();
  }
}
